// ⚠️ ARCHIVO GENERADO — NO LO EDITES AQUÍ.
// Fuente: /root/sites/_shared/lealify-campaigns/src/campaignsEndpoints.ts
// Para cambiarlo: edita la fuente y corre /root/sites/_shared/lealify-campaigns/sync.sh
// El deploy verifica esta copia con `sync.sh --check` y falla si difiere.
// lealify-campaigns/src/campaignsEndpoints.ts — FUENTE DE VERDAD
// ============================================================================
// LA SUPERFICIE HTTP del motor de campañas, en un solo sitio.
//
// Este archivo se COPIA a los dos fronts con ./sync.sh — no edites las copias.
//
// Por qué una FACTORÍA y no un cliente hecho: las dos fachadas usan transportes
// distintos y ninguno es "el bueno". La agenda hace `fetch` y lanza un Error con
// `code`/`quota` encima; el manager usa `baseApi`, que lanza `BaseApiError` con el
// cuerpo en `.data`. Unificar eso obligaría a tocar el manejo de errores de las dos
// (y el 429 de cupo se lee distinto en cada una). Aquí vive lo único que de verdad
// tiene que coincidir —QUÉ ruta, con QUÉ método y QUÉ cuerpo— y cada fachada inyecta
// su `request` y conserva su mapeo a modelo de pantalla, que sí es legítimamente suyo.
//
// El problema que resuelve: hasta el 24-jul-2026 cada endpoint nuevo se escribía dos
// veces y ya habían divergido — el manager no tenía `quota` ni `sendRow` (todo el
// envío asistido) y la agenda no tiene `runNow`, `cancel` ni nada de correo. Con esto,
// añadir un endpoint es una línea aquí y las dos fachadas lo ven.
// ============================================================================

/** Transporte que inyecta cada fachada. `path` va relativo a /api/campaigns. */
export type Requester = (
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
) => Promise<any>;

const enc = (v: string | number) => encodeURIComponent(String(v));

/**
 * Cliente del motor de campañas. Devuelve lo que responde el servidor, TAL CUAL:
 * el mapeo a modelo de pantalla se queda en cada fachada a propósito.
 */
export function crearClienteCampanas(request: Requester) {
  return {
    // ── Lectura ──
    listar: () => request('GET', ''),
    obtener: (id: string | number) => request('GET', `/${enc(id)}`),
    /** Filas de la cola. `status` filtra (pending/sent/failed/…). */
    cola: (id: string | number, opts: { status?: string; limit?: number } = {}) => {
      const p = new URLSearchParams();
      if (opts.status) p.set('status', opts.status);
      if (opts.limit) p.set('limit', String(opts.limit));
      const qs = p.toString();
      return request('GET', `/${enc(id)}/queue${qs ? `?${qs}` : ''}`);
    },
    /** Contactos descartados permanentemente de esta campaña. */
    descartados: (id: string | number) => request('GET', `/${enc(id)}/discarded`),
    /** A QUIÉN le tocaría si se programara ahora. Para BORRADORES: la cola —única lista
     *  con nombres— nace al programar, así que sin esto un borrador es un número a ciegas.
     *  Devuelve `{total, suppressed, contacts[]}`; `contacts` va recortado a `limit`. */
    audiencia: (id: string | number, limit?: number) =>
      request('GET', `/${enc(id)}/audience${limit ? `?limit=${limit}` : ''}`),
    /** Ritmo del NÚMERO + consumo del día. La velocidad no se elige: se muestra. */
    ritmo: () => request('GET', '/pace'),
    /** FATIGA (3-sep-2026): «N de estas personas recibieron OTRA campaña en los últimos
     *  `days` días». Sobre la cola pendiente si está programada, sobre la audiencia si es
     *  borrador. Solo informa — el motor nunca frena por esto (decisión Carlos). Devuelve
     *  `{days, total, afectados, ejemplos[], por_campana[]}`. */
    fatiga: (id: string | number, days?: number) =>
      request('GET', `/${enc(id)}/fatigue${days ? `?days=${days}` : ''}`),
    /** Cupo de envío asistido de una campaña. */
    cupo: (id: string | number) => request('GET', `/${enc(id)}/quota`),
    /** Resultados de una campaña de CORREO (entrega, aperturas, clics). Devuelve
     *  `{stats: null}` si la campaña no es de correo; `stats.clicks_tracked=false`
     *  = la campaña salió antes de que existiera el rastreo de clics (2-ago-2026)
     *  y la UI debe OCULTAR la línea de clics, no pintar un 0%. */
    resultadosCorreo: (id: string | number) => request('GET', `/${enc(id)}/email-stats`),
    contarAudiencia: (audiencia: unknown) => request('POST', '/preview-audience', audiencia),

    // ── Alta y edición ──
    crear: (datos: unknown) => request('POST', '', datos),
    editar: (id: string | number, datos: unknown) => request('PUT', `/${enc(id)}`, datos),
    /** Congela la cola: resuelve la audiencia y crea una fila por destinatario. */
    programar: (id: string | number) => request('POST', `/${enc(id)}/schedule`),
    // ⚠️ NO hay `DELETE /:id` en el motor: una campaña no se borra, se cancela o se
    // archiva (archivar conserva la cola). La agenda expone «eliminar» y por dentro
    // llama a cancelar — a propósito, para no perder el histórico de enviados.

    // ── Control del envío ──
    pausar: (id: string | number) => request('POST', `/${enc(id)}/pause`, {}),
    reanudar: (id: string | number) => request('POST', `/${enc(id)}/resume`, {}),
    cancelar: (id: string | number) => request('POST', `/${enc(id)}/cancel`, {}),
    /** Drena el cupo de hoy de una vez (uso manual). */
    enviarYa: (id: string | number) => request('POST', `/${enc(id)}/run-now`, {}),

    // ── Ficha (archivar NO toca la cola: es un eje aparte del envío) ──
    archivar: (id: string | number) => request('POST', `/${enc(id)}/archive`, {}),
    restaurar: (id: string | number) => request('POST', `/${enc(id)}/unarchive`, {}),

    // ── Envío ASISTIDO (lo dispara una persona; lo manda el servidor) ──
    /** `force:true` = override CONSCIENTE del gate anti-ban (code GATED con
     *  overridable:true): el operador confirmó enviar en frío. El servidor lo
     *  audita en wa_gate_log y el envío cuenta cupo como campaña. */
    enviarFila: (id: string | number, filaId: string | number, cuerpo?: { force?: boolean }) =>
      request('POST', `/${enc(id)}/queue/${enc(filaId)}/send`, cuerpo ?? {}),
    /** Manda a UNA persona SIN programar la campaña. El servidor materializa la fila de
     *  cola que falta y usa el mismo camino que el worker. Repone el botón «Enviar» que
     *  la lista de audiencia tenía antes de jun-2026. */
    enviarAContacto: (id: string | number, contactId: string | number, cuerpo?: { force?: boolean }) =>
      request('POST', `/${enc(id)}/send-contact`, { contact_id: Number(contactId), ...(cuerpo ?? {}) }),
    /** Camino manual por wa.me: solo MARCA como enviada, no envía. */
    marcarEnviada: (id: string | number, cuerpo: unknown) =>
      request('POST', `/${enc(id)}/queue/mark-sent`, cuerpo),
    desmarcarEnviada: (id: string | number, cuerpo: unknown) =>
      request('POST', `/${enc(id)}/queue/unmark-sent`, cuerpo),
    /** Variante por fila concreta (la otra recibe el contacto en el cuerpo). */
    marcarFilaEnviada: (id: string | number, filaId: string | number, cuerpo?: unknown) =>
      request('POST', `/${enc(id)}/queue/${enc(filaId)}/mark-sent`, cuerpo ?? {}),

    // ── Descarte permanente de un contacto (sobrevive a los ciclos) ──
    descartar: (id: string | number, cuerpo: unknown) => request('POST', `/${enc(id)}/discard`, cuerpo),
    recuperar: (id: string | number, cuerpo: unknown) => request('POST', `/${enc(id)}/undiscard`, cuerpo),

    // ── Variaciones anti-ban ──
    variacionesPreview: (id: string | number, cuerpo: unknown) =>
      request('POST', `/${enc(id)}/variations/preview`, cuerpo),
    variacionesGuardar: (id: string | number, cuerpo: unknown) =>
      request('POST', `/${enc(id)}/variations`, cuerpo),
    /** Reescribe las filas PENDIENTES con el pool ya aprobado. */
    variacionesAplicarPendientes: (id: string | number, cuerpo: unknown) =>
      request('POST', `/${enc(id)}/variations/apply-pending`, cuerpo),
    variarMensaje: (id: string | number, cuerpo: unknown) => request('POST', `/${enc(id)}/vary`, cuerpo),

    // ── Imagen adjunta ──
    subirImagen: (id: string | number, image: string) => request('POST', `/${enc(id)}/image`, { image }),
    borrarImagen: (id: string | number) => request('DELETE', `/${enc(id)}/image`),

    // ── Correo ──
    previewCorreo: (cuerpo: unknown) => request('POST', '/email-preview', cuerpo),
  };
}

export type ClienteCampanas = ReturnType<typeof crearClienteCampanas>;
